up.motion = (function () {
    const u = up.util;
    const e = up.element;
    const motionController = new up.MotionController('motion');
    const config = new up.Config(() => ({
        duration: 175,
        easing: 'ease',
        enabled: !matchMedia('(prefers-reduced-motion: reduce)').matches
    }));
    let namedAnimations = new up.Registry('animation', findAnimationFn);
    let namedTransitions = new up.Registry('transition', findTransitionFn);
    function reset() {
        motionController.reset();
    }
    async function animate(element, animationValue, { duration, easing, onFinished } = {}) {
        element = up.fragment.get(element);
        const animationFn = up.error.guardFn(findAnimationFn(animationValue));
        const timingOptions = effectiveTimingOptions({ duration, easing });
        let trackable = async function () {
            if (animationFn && canAnimateElement(element)) {
                try {
                    await animationFn(element, timingOptions);
                }
                finally {
                    onFinished?.();
                }
            }
            else {
                onFinished?.();
            }
        };
        await motionController.startFunction([element], trackable);
    }
    function canAnimateElement(element) {
        return !e.isSingleton(element);
    }
    function effectiveTimingOptions({ duration, easing }) {
        return {
            duration: config.enabled ? (duration ?? config.duration) : 0,
            easing: easing ?? config.easing,
        };
    }
    async function animateNow(element, lastFrame, { duration, easing, finishedFrame }) {
        let camelizedLastFrame = u.withRenamedKeys(lastFrame, u.kebabToCamelCase);
        let animation = element.animate([camelizedLastFrame], { duration, easing, fill: 'forwards' });
        let offFinish;
        let conclude = () => {
            animation.finish();
            e.setStyle(element, finishedFrame ?? lastFrame);
            animation.cancel();
            offFinish();
        };
        offFinish = up.on(element, 'up:motion:finish', conclude);
        await animation.finished;
        conclude();
    }
    function finish(element) {
        element = up.fragment.get(element);
        motionController.finish(element);
        return up.migrate.formerlyAsync?.('up.motion.finish()');
    }
    async function morph(oldElement, newElement, transitionValue, { duration, easing, afterInsert, beforeRemove, afterRemove } = {}) {
        oldElement = up.fragment.get(oldElement);
        newElement = up.fragment.get(newElement);
        const transitionFn = up.error.guardFn(findTransitionFn(transitionValue));
        const timingOptions = effectiveTimingOptions({ duration, easing });
        const trackable = async function ({ nested }) {
            if (transitionFn && canAnimateElement(oldElement)) {
                if (nested) {
                    await transitionFn(oldElement, newElement, timingOptions);
                }
                else {
                    const viewport = up.viewport.get(oldElement);
                    const scrollTopBeforeScroll = viewport.scrollTop;
                    const oldRemote = up.viewport.absolutize(oldElement, {
                        afterMeasure: () => e.insertBefore(oldElement, newElement)
                    });
                    afterInsert?.();
                    const scrollTopAfterScroll = viewport.scrollTop;
                    oldRemote.moveBounds(0, scrollTopAfterScroll - scrollTopBeforeScroll);
                    await transitionFn(oldElement, newElement, timingOptions);
                    beforeRemove?.();
                    oldRemote.bounds.remove();
                    afterRemove?.();
                }
            }
            else {
                beforeRemove?.();
                swapElementsDirectly(oldElement, newElement);
                afterInsert?.();
                afterRemove?.();
            }
        };
        await motionController.startFunction([newElement, oldElement], trackable);
    }
    function findTransitionFn(value) {
        if (isNone(value)) {
            return undefined;
        }
        else if (u.isFunction(value)) {
            return value;
        }
        else if (u.isArray(value)) {
            return composeTransitionFn(...value);
        }
        else if (u.isString(value) && value.includes('/')) {
            return composeTransitionFn(...value.split('/'));
        }
        else {
            return namedTransitions.get(value);
        }
    }
    function composeTransitionFn(oldAnimation, newAnimation) {
        if (!isNone(oldAnimation) && !isNone(newAnimation)) {
            const oldAnimationFn = findAnimationFn(oldAnimation) || u.asyncNoop;
            const newAnimationFn = findAnimationFn(newAnimation) || u.asyncNoop;
            return (oldElement, newElement, options) => Promise.all([
                oldAnimationFn(oldElement, options),
                newAnimationFn(newElement, options)
            ]);
        }
    }
    function findAnimationFn(value) {
        if (isNone(value)) {
            return undefined;
        }
        else if (u.isFunction(value)) {
            return value;
        }
        else if (u.isOptions(value)) {
            return (element, options) => animateNow(element, value, options);
        }
        else {
            return namedAnimations.get(value);
        }
    }
    const swapElementsDirectly = up.mockable(function (oldElement, newElement) {
        oldElement.replaceWith(newElement);
    });
    function motionOptions(element, options, parserOptions) {
        options = u.options(options);
        let parser = new up.OptionsParser(element, options, parserOptions);
        parser.booleanOrString('animation');
        parser.booleanOrString('transition');
        parser.string('easing');
        parser.number('duration');
        return options;
    }
    up.on('up:framework:boot', function () {
        if (!config.enabled) {
            up.puts('up.motion', 'Animations are disabled');
        }
    });
    function isNone(motionValue) {
        return !motionValue || motionValue === 'none';
    }
    function registerOpacityAnimation(name, from, to) {
        namedAnimations.put(name, function (element, options) {
            element.style.opacity = u.evalOption(from, element);
            return animateNow(element, { opacity: to }, options);
        });
    }
    function currentOpacity(element) {
        return e.style(element, 'opacity');
    }
    registerOpacityAnimation('fade-in', 0, 1);
    registerOpacityAnimation('fade-out', currentOpacity, 0);
    function translateCSS(dx, dy) {
        return { transform: `translate(${dx}px, ${dy}px)` };
    }
    function zeroTranslateCSS() {
        return { transform: 'translate(0, 0)' };
    }
    function untranslatedBox(element) {
        e.setStyle(element, zeroTranslateCSS());
        return element.getBoundingClientRect();
    }
    function registerMoveAnimations(direction, boxToTransform) {
        const animationToName = `move-to-${direction}`;
        const animationFromName = `move-from-${direction}`;
        namedAnimations.put(animationToName, function (element, options) {
            const box = untranslatedBox(element);
            const transform = boxToTransform(box);
            return animateNow(element, transform, options);
        });
        namedAnimations.put(animationFromName, function (element, options) {
            const box = untranslatedBox(element);
            const transform = boxToTransform(box);
            e.setStyle(element, transform);
            return animateNow(element, zeroTranslateCSS(), { ...options, finishedFrame: { transform: '' } });
        });
    }
    registerMoveAnimations('top', function (box) {
        const travelDistance = box.top + box.height;
        return translateCSS(0, -travelDistance);
    });
    registerMoveAnimations('bottom', function (box) {
        const travelDistance = up.viewport.rootHeight() - box.top;
        return translateCSS(0, travelDistance);
    });
    registerMoveAnimations('left', function (box) {
        const travelDistance = box.left + box.width;
        return translateCSS(-travelDistance, 0);
    });
    registerMoveAnimations('right', function (box) {
        const travelDistance = up.viewport.rootWidth() - box.left;
        return translateCSS(travelDistance, 0);
    });
    namedTransitions.put('cross-fade', ['fade-out', 'fade-in']);
    namedTransitions.put('move-left', ['move-to-left', 'move-from-right']);
    namedTransitions.put('move-right', ['move-to-right', 'move-from-left']);
    namedTransitions.put('move-up', ['move-to-top', 'move-from-bottom']);
    namedTransitions.put('move-down', ['move-to-bottom', 'move-from-top']);
    up.on('up:framework:reset', reset);
    return {
        morph,
        animate,
        finish,
        transition: namedTransitions.put,
        animation: namedAnimations.put,
        config,
        isNone,
        swapElementsDirectly,
        motionOptions,
    };
})();
up.transition = up.motion.transition;
up.animation = up.motion.animation;
up.morph = up.motion.morph;
up.animate = up.motion.animate;
